import { DELETE, GET, PATCH, POST, PUT } from '@/shared/api/requests';
import type {
  AdminProjectSummary,
  AdministratorsUpdateInput,
  ClientProjectMembership,
  MembershipInput,
  MembershipUpdateInput,
  OrganizationUser,
  PermissionRequestCreateInput,
  PermissionRequestReviewInput,
  ProjectMembership,
  ProjectPermissionRequest,
  ProjectRole,
  RoleInput,
  RolePermission,
  UserSummary,
} from '../types';

// ---------------------------------------------------------------------------
// Cross-project listings
// ---------------------------------------------------------------------------

export const getClientProjectsForAdmin = async (): Promise<AdminProjectSummary[]> => {
  const response = await GET('/rbac/client-projects/');
  return (response.data?.projects ?? []) as AdminProjectSummary[];
};

export const getClientProjectMembersForAdmin = async (): Promise<ClientProjectMembership[]> => {
  const response = await GET('/rbac/client-project-members/');
  return (response.data?.memberships ?? []) as ClientProjectMembership[];
};

export const getClientOrganizationMembers = async (): Promise<OrganizationUser[]> => {
  const response = await GET('/rbac/client-organization-members/');
  return (response.data?.users ?? []) as OrganizationUser[];
};

export const deactivateClientOrganizationMember = async (
  userId: number,
): Promise<OrganizationUser> => {
  const response = await POST(`/rbac/client-organization-members/${userId}/deactivate/`, {});
  return response.data?.user as OrganizationUser;
};

export const getMyAdminProjects = async (): Promise<AdminProjectSummary[]> => {
  const response = await GET('/rbac/my-admin-projects/');
  return (response.data?.projects ?? []) as AdminProjectSummary[];
};

// ---------------------------------------------------------------------------
// Permissions (read-only)
// ---------------------------------------------------------------------------

export const getProjectPermissions = async (projectId: number): Promise<RolePermission[]> => {
  const response = await GET(`/rbac/projects/${projectId}/permissions/`);
  return (response.data?.permissions ?? []) as RolePermission[];
};

export const getProjectPermissionRequests = async (
  projectId: number,
  status?: string,
): Promise<ProjectPermissionRequest[]> => {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await GET(`/rbac/projects/${projectId}/permission-requests/${query}`);
  return (response.data?.permission_requests ?? []) as ProjectPermissionRequest[];
};

export const createProjectPermissionRequest = async (
  projectId: number,
  payload: PermissionRequestCreateInput,
): Promise<ProjectPermissionRequest> => {
  const response = await POST(`/rbac/projects/${projectId}/permission-requests/`, payload);
  return response.data as ProjectPermissionRequest;
};

export const reviewProjectPermissionRequest = async (
  projectId: number,
  requestId: number,
  payload: PermissionRequestReviewInput,
): Promise<ProjectPermissionRequest> => {
  const response = await PATCH(
    `/rbac/projects/${projectId}/permission-requests/${requestId}/`,
    payload,
  );
  return response.data as ProjectPermissionRequest;
};

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const getProjectRoles = async (projectId: number): Promise<ProjectRole[]> => {
  const response = await GET(`/rbac/projects/${projectId}/roles/`);
  return (response.data?.roles ?? []) as ProjectRole[];
};

export const createProjectRole = async (
  projectId: number,
  payload: RoleInput,
): Promise<ProjectRole> => {
  const response = await POST(`/rbac/projects/${projectId}/roles/`, payload);
  return response.data as ProjectRole;
};

export const updateProjectRole = async (
  projectId: number,
  roleId: number,
  payload: Partial<RoleInput>,
): Promise<ProjectRole> => {
  const response = await PATCH(`/rbac/projects/${projectId}/roles/${roleId}/`, payload);
  return response.data as ProjectRole;
};

export const deleteProjectRole = async (projectId: number, roleId: number): Promise<void> => {
  await DELETE(`/rbac/projects/${projectId}/roles/${roleId}/`);
};

// ---------------------------------------------------------------------------
// Memberships
// ---------------------------------------------------------------------------

export const getProjectMemberships = async (projectId: number): Promise<ProjectMembership[]> => {
  const response = await GET(`/rbac/projects/${projectId}/memberships/`);
  return (response.data?.memberships ?? []) as ProjectMembership[];
};

export const upsertProjectMembership = async (
  projectId: number,
  payload: MembershipInput,
): Promise<ProjectMembership> => {
  const response = await POST(`/rbac/projects/${projectId}/memberships/`, payload);
  return response.data as ProjectMembership;
};

export const deleteProjectMembership = async (
  projectId: number,
  membershipId: number,
): Promise<void> => {
  await DELETE(`/rbac/projects/${projectId}/memberships/${membershipId}/`);
};

export const updateProjectMembership = async (
  projectId: number,
  membershipId: number,
  payload: MembershipUpdateInput,
): Promise<ProjectMembership> => {
  const response = await PATCH(`/rbac/projects/${projectId}/memberships/${membershipId}/`, payload);
  return response.data as ProjectMembership;
};

export { deactivateOwnProjectMembership } from '@/shared/api/base-api';

// ---------------------------------------------------------------------------
// Administrators
// ---------------------------------------------------------------------------

export const getProjectAdministrators = async (projectId: number): Promise<UserSummary[]> => {
  const response = await GET(`/rbac/projects/${projectId}/administrators/`);
  return (response.data?.administrators ?? []) as UserSummary[];
};

export const updateProjectAdministrators = async (
  projectId: number,
  payload: AdministratorsUpdateInput,
): Promise<UserSummary[]> => {
  const response = await PUT(`/rbac/projects/${projectId}/administrators/`, payload);
  return (response.data?.administrators ?? []) as UserSummary[];
};

// ---------------------------------------------------------------------------
// Shareable users (kept on /base/...). Powers the membership user picker.
// ---------------------------------------------------------------------------

export const getProjectUsers = async (projectId: number): Promise<UserSummary[]> => {
  const response = await GET(`/base/projects/${projectId}/shareable-users/`);
  return (response.data?.users ?? []) as UserSummary[];
};
